import { StaticQuery, graphql } from 'gatsby';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import React from 'react';

export const Seo = ({ pathname, title, description, image, article: metaArticle, meta = [], howToGuide, calculatorSchema })  => {
    return (
        <StaticQuery
            query={ SEO_QUERY }
            render={ data => {
                const { site: { siteMetadata, buildTime } } = data;
                let howToSchemaNode = howToGuide ? howToGuide[0] : null;
                const metaDescription = description || siteMetadata.description;
                const metaTitle = title || siteMetadata.title;
                const metaUrl = `${siteMetadata.siteUrl}${pathname}`;
                const metaCanonical = pathname === '/' ? `${siteMetadata.siteUrl}` : `${siteMetadata.siteUrl}${pathname}`;
                const curYear = new Date().getFullYear();

                const indexValue = pathname.endsWith("/result") ? "noindex" : "index";

                const logo = `${siteMetadata.siteUrl}/${siteMetadata.logo}`;
                let metaImage = metaArticle?.image || image || `/${siteMetadata.linkPreviewImage}`;

                if( metaImage.indexOf( 'images.prismic' ) === -1 ) {
                    metaImage = `${siteMetadata.siteUrl}${metaImage}`
                }

                const metaImageAlt = image && image.alt ? image.alt : metaDescription;

                const getSchemaOrgWebsite = () => ({
                    '@type': 'WebSite',
                    '@id': `${siteMetadata.siteUrl}/#organization`,
                    'url': siteMetadata.siteUrl,
                    'name': siteMetadata.title,
                    'description': siteMetadata.description,
                    'copyrightYear': `${curYear}`,
                    'image': logo,
                    'sameAs': [siteMetadata.social.twitterUrl, siteMetadata.social.facebookUrl],
                    'copyrightHolder': [
                        {
                            '@type': 'Organization',
                            'url': siteMetadata.copyright.siteUrl,
                            'name': siteMetadata.copyright.title,
                            'contactPoint': [
                                {
                                    '@type': 'ContactPoint',
                                    'url': siteMetadata.copyright.siteUrl,
                                    'email': siteMetadata.contact.email,
                                    'contactType': 'technical support',
                                },
                            ],
                        },
                    ],
                });

                const getSchemaOrgWebpage = () => ({
                    '@type': 'WebPage',
                    '@id': metaUrl,
                    'isPartOf': {
                        '@id': `${siteMetadata.siteUrl}/#organization`,
                    },
                    'url': metaUrl,
                    'name': metaTitle,
                    'description': metaDescription,
                    'lastReviewed': buildTime,
                    'sameAs': [siteMetadata.social.twitterUrl, siteMetadata.social.facebookUrl],
                });

                const getSchemaOrgArticle = () => ({
                    '@type': 'Article',
                    'publisher': {
                        '@type': 'Organization',
                        'name': siteMetadata.title,
                        '@id': `${siteMetadata.siteUrl}/organization`,
                        'logo': {
                            '@type': 'ImageObject',
                            'url': logo,
                        },
                    },
                    'mainEntityOfPage': {
                        '@type': 'WebPage',
                        '@id': metaUrl,
                    },
                    'headline': metaArticle.title,
                    'description': metaArticle.description,
                    'datePublished': metaArticle.date,
                    'dateModified': metaArticle.date,
                    'author': {
                        '@type': 'Organization',
                        'name': siteMetadata.title,
                    },
                    'image': metaImage || logo,
                });

                const getSchemaOrgJson = () => {
                    const schema = {
                        '@context': 'https://schema.org',
                        '@graph': [getSchemaOrgWebsite()].concat(metaArticle ? [getSchemaOrgArticle()] : [getSchemaOrgWebpage()]),
                    };

                    
                    if (pathname.includes('/calculator/') && howToSchemaNode?.data?.how_to_schema?.text) {
                        const parsedSchema = JSON.parse(howToSchemaNode.data.how_to_schema.text);
                        if (parsedSchema) {
                            schema['@graph'].push(parsedSchema);
                        }
                    }

                    if (pathname === '/calculator' && calculatorSchema.length > 0) {
                        calculatorSchema.forEach(schemaNode => 
                        {
                            try {
                                const parsedSchema = JSON.parse(schemaNode.data.schema.text);
                                schema['@graph'].push(parsedSchema);
                            } catch (error) {
                                console.error("Error parsing schema JSON:", error);
                            }
                        })
                    }

                    return JSON.stringify(schema);
                };

                return (
                    <Helmet
                        htmlAttributes={ {
                            lang: `en`,
                        } }
                        title={ metaTitle }
                        titleTemplate={ `%s · ${siteMetadata.title}` }
                        link = {[
                            {
                                rel: 'canonical',
                                href: `${metaCanonical}`
                            }
                        ]}
                        meta={ [
                            {
                                property: `robots`,
                                content: indexValue,
                            },
                            {
                                property: `og:title`,
                                content: metaTitle,
                            },
                            {
                                name: `description`,
                                content: metaDescription,
                            },
                            {
                                property: `og:description`,
                                content: metaDescription,
                            },
                            {
                                property: `og:site_name`,
                                content: siteMetadata.title,
                            },
                            {
                                property: `og:url`,
                                content: metaUrl,
                            },
                            {
                                name: `twitter:domain`,
                                content: `@${siteMetadata.siteUrl}`,
                            },
                            {
                                name: `twitter:site`,
                                content: `@${siteMetadata.social.twitterHandle}`,
                            },
                            {
                                name: `twitter:creator`,
                                content: `@${siteMetadata.social.twitterHandle}`,
                            },
                            {
                                name: `twitter:title`,
                                content: metaTitle,
                            },
                            {
                                name: `twitter:description`,
                                content: metaDescription,
                            },
                            {
                                property: `fb:admins`,
                                content: siteMetadata.social.facebookId,
                            },
                        ]
                            .concat(
                                metaImage
                                    ? [
                                        {
                                            property: 'og:image',
                                            content: metaImage,
                                        },
                                        {
                                            property: 'og:image:alt',
                                            content: metaImageAlt,
                                        },
                                        {
                                            name: 'twitter:card',
                                            content: 'summary_large_image',
                                        },
                                        {
                                            name: 'twitter:image',
                                            content: metaImage,
                                        },
                                        {
                                            name: 'twitter:image:alt',
                                            content: metaImageAlt,
                                        },
                                    ]
                                    : [
                                        {
                                            name: 'twitter:card',
                                            content: 'summary',
                                        },
                                    ]
                            )
                            .concat(
                                metaArticle
                                    ? [
                                        {
                                            property: `og:type`,
                                            content: `article`,
                                        },
                                        {
                                            property: `article:published_time`,
                                            content: metaArticle.date,
                                        },
                                        {
                                            property: `article:modified_time`,
                                            content: metaArticle.date,
                                        },
                                        {
                                            property: `article:author`,
                                            content: metaArticle.author,
                                        },
                                        {
                                            property: `article:section`,
                                            content: metaArticle.category || 'Payroll',
                                        },
                                        {
                                            property: `article:publisher`,
                                            content: siteMetadata.social.facebookUrl,
                                        },
                                    ]
                                    : [
                                        {
                                            property: `og:type`,
                                            content: `website`,
                                        },
                                    ]
                            )
                            .concat(meta) }
                        script={ [
                            {
                                type: 'application/ld+json',
                                innerHTML: getSchemaOrgJson(),
                            },
                        ] }
                    />
                );
            } }
        />
    )
}

const SEO_QUERY = graphql`
    query {
        site {
            buildTime
            siteMetadata {
                title
                description
                siteUrl
                logo
                linkPreviewImage
                contact {
                    email
                }
                social {
                    twitterHandle
                    twitterUrl
                    facebookUrl
                    facebookId
                }
                copyright {
                    title
                    siteUrl
                    logo
                }
            }
        }
    }
`;

Seo.propTypes = {
    pathname: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    image: PropTypes.string,
    article: PropTypes.object,
    meta: PropTypes.arrayOf(PropTypes.object),
    schema: PropTypes.arrayOf(PropTypes.object),
};

export default Seo;
